import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { diaryService } from '../services/diary';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  BookOpenIcon,
  HeartIcon,
  ShareIcon,
  EyeIcon,
  CalendarIcon,
  // ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';

const Dashboard = () => {
  const { user } = useAuth();
  const [recentEntries, setRecentEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [entriesResponse, statsResponse] = await Promise.all([
          diaryService.getAllEntries({ page: 1, limit: 5 }),
          diaryService.getStats()
        ]);
        
        setRecentEntries(entriesResponse.data.entries);
        setStats(statsResponse);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const quickActions = [
    {
      title: 'New Entry',
      description: 'Start writing today',
      icon: PlusIcon,
      to: '/entries/new',
      color: 'bg-primary-500',
      hoverColor: 'hover:bg-primary-600'
    },
    {
      title: 'Search',
      description: 'Find your memories',
      icon: MagnifyingGlassIcon,
      to: '/search',
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600'
    },
    {
      title: 'All Entries',
      description: 'Browse your journal',
      icon: BookOpenIcon,
      to: '/entries',
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600'
    }
  ];

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const getMoodEmoji = (mood) => {
    const moodMap = {
      'happy': '😊',
      'sad': '😢',
      'excited': '😍',
      'angry': '😡',
      'peaceful': '😌',
      'anxious': '😰',
      'thoughtful': '🤔',
      'tired': '😴'
    };
    return moodMap[mood?.toLowerCase()] || mood || '📝';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-serif font-bold mb-2">
              {getGreeting()}, {user?.name?.split(' ')[0]}! 👋
            </h1>
            <p className="text-primary-100 text-lg">
              Ready to capture today's thoughts and memories?
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Actions */}
        <motion.section
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h2 className="text-2xl font-serif font-bold text-ink-black mb-6">
            Quick Actions
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {quickActions.map((action, index) => (
              <motion.div
                key={action.title}
                whileHover={{ scale: 1.02, y: -5 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={action.to}
                  className={`block p-6 rounded-xl ${action.color} ${action.hoverColor} text-white transition-all duration-300 shadow-lg hover:shadow-xl`}
                >
                  <action.icon className="h-8 w-8 mb-3" />
                  <h3 className="text-xl font-semibold mb-1">{action.title}</h3>
                  <p className="text-white/80">{action.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Stats Overview */}
        {stats && (
          <motion.section
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2 className="text-2xl font-serif font-bold text-ink-black mb-6">
              Your Journey
            </h2>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="card p-6 text-center">
                <BookOpenIcon className="h-8 w-8 text-primary-600 mx-auto mb-3" />
                <h3 className="text-2xl font-bold text-ink-black">
                  {stats.overview.totalEntries}
                </h3>
                <p className="text-gray-600">Total Entries</p>
              </div>
              <div className="card p-6 text-center">
                <HeartIcon className="h-8 w-8 text-red-500 mx-auto mb-3" />
                <h3 className="text-2xl font-bold text-ink-black">
                  {stats.overview.pinnedEntries}
                </h3>
                <p className="text-gray-600">Pinned Entries</p>
              </div>
              <div className="card p-6 text-center">
                <ShareIcon className="h-8 w-8 text-blue-500 mx-auto mb-3" />
                <h3 className="text-2xl font-bold text-ink-black">
                  {stats.overview.sharedEntries}
                </h3>
                <p className="text-gray-600">Shared Entries</p>
              </div>
              <div className="card p-6 text-center">
                <EyeIcon className="h-8 w-8 text-green-500 mx-auto mb-3" />
                <h3 className="text-2xl font-bold text-ink-black">
                  {stats.overview.totalViews}
                </h3>
                <p className="text-gray-600">Total Views</p>
              </div>
            </div>
          </motion.section>
        )}

        {/* Recent Entries */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-serif font-bold text-ink-black">
              Recent Entries
            </h2>
            <Link
              to="/entries"
              className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              View all →
            </Link>
          </div>

          {recentEntries.length > 0 ? (
            <div className="space-y-4">
              {recentEntries.map((entry, index) => (
                <motion.div
                  key={entry._id}
                  className="card p-6 hover:shadow-lg transition-all duration-300"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ x: 5 }}
                >
                  <Link to={`/entries/${entry._id}`} className="block">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-2xl">
                            {getMoodEmoji(entry.mood)}
                          </span>
                          <h3 className="text-lg font-semibold text-ink-black">
                            {entry.title}
                          </h3>
                          {entry.isPinned && (
                            <HeartIcon className="h-4 w-4 text-red-500 fill-current" />
                          )}
                        </div>
                        <p className="text-gray-600 mb-3 line-clamp-2">
                          {entry.content.replace(/<[^>]*>/g, '').substring(0, 150)}...
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span className="flex items-center space-x-1">
                              <CalendarIcon className="h-4 w-4" />
                              <span>{format(new Date(entry.createdAt), 'MMM d, yyyy')}</span>
                            </span>
                            {entry.folder && (
                              <span className="bg-primary-100 text-primary-700 px-2 py-1 rounded-full text-xs">
                                {entry.folder}
                              </span>
                            )}
                          </div>
                          <div className="flex space-x-1">
                            {entry.tags.slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs"
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              className="card p-12 text-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <BookOpenIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                No entries yet
              </h3>
              <p className="text-gray-500 mb-6">
                Start your journaling journey by creating your first entry
              </p>
              <Link to="/entries/new" className="btn-primary">
                <PlusIcon className="h-5 w-5 mr-2" />
                Create First Entry
              </Link>
            </motion.div>
          )}
        </motion.section>
      </div>
    </div>
  );
};

export default Dashboard;
